import type { StoreCopy } from './audio-course-client';

// The 17-block LDP (long-form direct-response) framework. Order + labels are
// Michael's; the interface renders one field per block, pre-populated.
export const STORE_BLOCKS: Array<{ key: string; label: string; hint?: string }> = [
  { key: 'b1', label: '1 · Call out the audience' },
  { key: 'b2', label: '2 · Big bold benefit headline' },
  { key: 'b3', label: '3 · Sub-headlines' },
  { key: 'b4', label: '4 · Intrigue / open loops' },
  { key: 'b5', label: '5 · Floodlight on the problem' },
  { key: 'b6', label: '6 · Solutions in the marketplace' },
  { key: 'b7', label: '7 · Our credentials' },
  { key: 'b8', label: '8 · Benefits' },
  { key: 'b9', label: '9 · Social proof', hint: 'Needs real testimonials / rating / learner count.' },
  { key: 'b10', label: '10 · Godfather offer' },
  { key: 'b11', label: '11 · Bonuses' },
  { key: 'b12', label: '12 · Value stack' },
  { key: 'b13', label: '13 · Price reveal' },
  { key: 'b14', label: '14 · Scarcity' },
  { key: 'b15', label: '15 · Guarantee' },
  { key: 'b16', label: '16 · Call to action' },
  { key: 'b17', label: '17 · P.S.' },
];

export function emptyStore(productId = ''): StoreCopy {
  const blocks: Record<string, string> = {};
  for (const b of STORE_BLOCKS) blocks[b.key] = '';
  return {
    title: '', author: 'Glossika', product_id: productId, cover_image_url: '',
    vars: { anchor_high: '', anchor_low: '', your_price: '', bundle_price: '', countdown: '', donation_amount: '' },
    blocks, bonuses: '', bonus_covers: '', upsell: '', fund_line: '', sample_audio: '', sample_pages: '',
    success_instructions: '', success_upsell: '',
  };
}

// Seeded content for audio:CMN_TW:A1 — the copy Michael approved. Markup:
// **bold**, ==highlight==, and **==term==** for the branded (bold+highlight) terms.
export const CMN_TW_A1_STORE: StoreCopy = {
  title: 'Mandarin (Taiwan): Travel A1',
  author: 'Glossika',
  product_id: 'audio:CMN_TW:A1',
  cover_image_url: '',
  vars: {
    anchor_high: '$400', anchor_low: '$200', your_price: '$50',
    bundle_price: '$150', countdown: '48 hours', donation_amount: '',
  },
  blocks: {
    b1: 'DO YOUR NEXT HOLIDAY TO TAIWAN IN CHINESE',
    b2: 'SPEAK CHINESE IN 6 WEEKS',
    b3: `No classes. No textbooks. No staring at a screen. Just your ears, 75 minutes a day. In fact, I guarantee I can get you speaking without understanding a single Chinese character. This is exactly what you need to prepare for your trip: the things you'll actually say in Taiwan.
- Ordering food
- Asking directions
- Buying tickets
- Visiting temples
- Getting around the night markets

Every phrase is drilled until it comes out on its own. And every phrase is informative, so you'll end up knowing a lot about Taiwan and how to talk about it in Chinese.`,
    b4: `Travellers who come home from Taiwan speaking Chinese didn't study more than you. They did one thing differently. With this course you will never forget how to say a sentence, and it takes just one minute of training, three days in a row. No other course delivers this. No other course is even willing to guarantee that it works.`,
    b5: `Chinese is very different, and it is tough. Nobody understands you if you can only say single words. That is why vocabulary alone won't help you, and why memorising flashcards won't help you. This course puts everything into context, so anyone can understand what you're asking for or talking about. You get ear training and a step-by-step buildup of the easiest patterns in Chinese, so you start speaking from the very first day. Only this course makes you understand every word and every grammar particle in the exact order you're trained to say them out loud, in the correct accent, making yourself understood 100% of the time.`,
    b6: `You won't gain fluency from Duolingo, Anki flashcards, or other gamified subscription apps. Maybe you've already tried. No other app delivers this much efficiency in so little time. Here is why the alternatives fall short:
- Group classes: expensive, slow, paced to the weakest student, and gone the week you travel.
- Private tutors: $30–60 an hour, and you still do the hard recall alone.
- Phrasebooks: you flip, you mispronounce, they smile and switch to English.

None of them train the one thing travel needs: instant, hands-free recall.`,
    b7: `Our founder has a background in linguistics and has been training dozens of languages for over 40 years. He is the only founder of a language-learning company who speaks unwritten indigenous languages at high fluency. Over a million people have signed up on his language apps and websites, so he knows how to train anyone to acquire languages by ear. Michael coined the terms **==Sentence Mining==** and training languages by **==reps==**, terms the polyglot YouTube gurus now use freely. And now Michael brings you his **==Gloss Framework==** (patent pending): you think of every sentence you want to say in terms of glosses, and from those you produce and speak your target language with ease.`,
    b8: `- 50 patterns, each with 20 questions and answers: 2,000 sentences in all.
- Every pattern gets 3 days of training.
- The 3-day cycle takes every sentence from "never heard it" to "said it without thinking" in 72 hours.
- 150 audio files, 55 hours, done over 6 weeks.
- Complexity builds gradually.
- Glossika level A1 is equivalent to at least HSK 1–3.
- Guided audio buildup, so you learn eyes-free: on the plane, in the taxi, walking to dinner.
- You'll understand real Taiwan voices the moment they speak.
- Companion books (PDF and EPUB), so you can check the written word and pronunciation after your ear already knows the sound.`,
    b9: `*[Needs real testimonials, a rating, and a learner count.]*`,
    b10: `You do the six weeks of training. You travel to Taiwan. If you can't order food, buy tickets, and get directions, all in Chinese, email us and we'll refund you. You keep everything.`,
    b11: `- Taiwan Chinese A0 pronunciation guide
- Taiwan Chinese A1 guide
- Taiwan Chinese high-function-word poster
- Interactive mindmap of the most essential functional vocabulary in Mandarin
- Glossika Chinese Pronunciation and Tone Training`,
    b12: `- Private tutoring: $400 or more.
- Group courses: $200–300, plus three months of your evenings.
- AI: $1,000 or more. Hundreds of hours wasted on trial-and-error training, hundreds of dollars in tokens, hundreds more in audio, and it's still riddled with errors.
- You are getting 2,000 sentences (half male, half female), 50-plus hours of audio training, the companion book, and bonuses worth over $500.`,
    b13: `This audio course tutors you to A1 travel fluency for a fraction of the cost of a tutor. Not {{anchor_high}}. Not {{anchor_low}}. Just **{{your_price}}**, the low introductory price.`,
    b14: `Pre-order A1–B2 as a bundle for just **{{bundle_price}}** today. These prices end in {{countdown}}.`,
    b15: `50-day speak-or-refund. Train for six weeks. If you're not asking and answering questions in Chinese, we refund you in full and you keep the product. The risk is entirely ours.`,
    b16: `Check out and download the first day's training. You'll be answering Chinese questions before bed tonight.`,
    b17: `Taiwan is the easiest place in Asia to travel and the friendliest place to try your Chinese. Six weeks from now you could be ordering taro balls at the night market instead of pointing at them. 2,000 real sentences, trained into your ear, for {{your_price}}, yours to keep, with a full refund if it doesn't work. Buy Now.`,
  },
  bonuses: `Taiwan Chinese A0 pronunciation guide
Taiwan Chinese A1 guide
Taiwan Chinese high-function-word poster
Interactive mindmap of the most essential functional vocabulary in Mandarin
Glossika Chinese Pronunciation and Tone Training`,
  bonus_covers: '',
  upsell: `Donate to the Glossika Indigenous Language Fund: {{donation_amount}}.`,
  fund_line: `Proceeds from this sale will go towards the Glossika Indigenous Language Fund, where your support preserves indigenous languages and sustains indigenous cultures.`,
  sample_audio: `https://d3fq9gfl03cg5d.cloudfront.net/audio/course/samples/cmn_A1_p12_d1_s0_60.mp3
https://d3fq9gfl03cg5d.cloudfront.net/audio/course/samples/cmn_A1_p20_d2_s0_60.mp3
https://d3fq9gfl03cg5d.cloudfront.net/audio/course/samples/cmn_A1_p30_d3_s0_60.mp3`,
  sample_pages: `https://d3fq9gfl03cg5d.cloudfront.net/audio/course/samples/companion_CMN_TW_A1_p12.png
https://d3fq9gfl03cg5d.cloudfront.net/audio/course/samples/companion_CMN_TW_A1_p20.png`,
  success_instructions: `Inside each level you'll find 50 patterns, three MP3s each labeled Day 1, Day 2, Day 3. There are three kinds of training: 1) through word-by-word glosses of the target language, 2) through full sentence glosses of the target language, 3) through natural translation from English skipping the gloss step. All three kinds are trained on the first day files, day 2 files train via full-sentence gloss, and day 3 files via natural English.

For best results we recommend dictating everything you hear on paper on the day 1 training, and checking your work with the text provided. This will improve your listening for catching any errors early on.

The course is designed for one new pattern per day, plus two review sessions from the previous two days. You can finish the course in less time if you do 2 patterns per day, if you can commit more than 2 hours per day. You'll get best results by being consistent, doing the training 7 days per week until completed.`,
  success_upsell: `If you wish to get an extra boost, we recommend signing up for Glossika's apps which include interactive exercises. With today's purchase, we're offering a special 24% off annual packages: normally selling for $299.99, a one-time offer available right now is just $227.99 which gives you a 12-month subscription to Glossika's apps.`,
};

// Product -> seeded default. Keyed by `${lang}:${level}`.
export const STORE_SEEDS: Record<string, StoreCopy> = {
  'CMN:A1': CMN_TW_A1_STORE,
};

// Assemble the full markdown package (what gets pasted into Blitzscale / copied).
export function assembleStoreMarkdown(s: StoreCopy): string {
  const L: string[] = [];
  L.push(`# Store content: ${s.product_id || '(product)'}`);
  L.push('');
  L.push('**Markup:** `**bold**` = bold; `==highlight==` = the store\'s highlight colour; branded terms are both: `**==term==**`.');
  L.push('');
  L.push(`- Title: **${s.title}**`);
  L.push(`- Author: **${s.author}**`);
  L.push(`- product_id / delivery_key: **${s.product_id}**`);
  if (s.cover_image_url.trim()) L.push(`- Cover image: ${s.cover_image_url.trim()}`);
  L.push('');
  const v = s.vars;
  L.push('## Template variables (store injects)');
  L.push(`anchor_high = ${v.anchor_high}, anchor_low = ${v.anchor_low}, your_price = ${v.your_price}, bundle_price = ${v.bundle_price}, countdown = ${v.countdown}, donation_amount = ${v.donation_amount}`);
  L.push('');
  if (s.sample_audio.trim()) {
    L.push('## Audio samples (1 minute each)');
    for (const u of s.sample_audio.split('\n').map((x) => x.trim()).filter(Boolean)) L.push(`- ${u}`);
    L.push('');
  }
  if (s.sample_pages.trim()) {
    L.push('## Sample book pages');
    for (const u of s.sample_pages.split('\n').map((x) => x.trim()).filter(Boolean)) L.push(`- ${u}`);
    L.push('');
  }
  L.push('## Copy (LDP, 17 blocks)');
  L.push('');
  for (const b of STORE_BLOCKS) {
    L.push(`**${b.label}**`);
    L.push(s.blocks[b.key] || '');
    L.push('');
  }
  if (s.upsell.trim() || s.fund_line.trim()) {
    L.push('## Upsell and fund');
    if (s.upsell.trim()) L.push(`- Order-bump / upsell: "${s.upsell.trim()}"`);
    if (s.fund_line.trim()) L.push(`- Bottom-of-page line: "${s.fund_line.trim()}"`);
    L.push('');
  }
  if (s.bonuses.trim() || s.bonus_covers.trim()) {
    L.push('## Bonuses (bundled with the product)');
    for (const x of s.bonuses.split('\n').map((y) => y.trim()).filter(Boolean)) L.push(`- ${x}`);
    for (const u of s.bonus_covers.split('\n').map((y) => y.trim()).filter(Boolean)) L.push(`- Bonus cover: ${u}`);
    L.push('');
  }
  if (s.success_instructions.trim() || s.success_upsell.trim()) {
    L.push('## Post-purchase success page');
    if (s.success_instructions.trim()) {
      L.push('### How to use the product');
      L.push(s.success_instructions.trim());
      L.push('');
    }
    if (s.success_upsell.trim()) {
      L.push('### Pro/Plus subscription upsell');
      L.push(s.success_upsell.trim());
      L.push('');
    }
  }
  return L.join('\n').replace(/\n{3,}/g, '\n\n').trim() + '\n';
}

// ---- Blitzscale store payload -----------------------------------------------
// Serialize the store copy into Blitzscale's Publish payload (their spec). Markup is sent
// RAW (**bold**, ==highlight==); price {{tokens}} stay in the copy; the four numeric
// suggested_prices are parsed out separately. The backend adds status/version + the key.
const nonEmpty = (s: string) => (s || '').split('\n').map((x) => x.trim()).filter(Boolean);

function splitBlock(md: string): { text: string; list: string[]; text_after: string } {
  const text: string[] = [], list: string[] = [], after: string[] = [];
  let phase: 'pre' | 'list' | 'post' = 'pre';
  for (const t of (md || '').split('\n').map((x) => x.trim())) {
    const bullet = /^[-*]\s+/.test(t);
    if (phase === 'pre') { if (bullet) { phase = 'list'; list.push(t.replace(/^[-*]\s+/, '')); } else if (t) text.push(t); }
    else if (phase === 'list') { if (bullet) list.push(t.replace(/^[-*]\s+/, '')); else if (t) { phase = 'post'; after.push(t); } }
    else if (t) after.push(t.replace(/^[-*]\s+/, ''));
  }
  return { text: text.join(' ').trim(), list, text_after: after.join(' ').trim() };
}

function valueStack(md: string): { rows: Array<{ label: string; value: string }>; footer: string } {
  const rows: Array<{ label: string; value: string }> = [];
  const footer: string[] = [];
  for (const raw of nonEmpty(md)) {
    const b = raw.replace(/^[-*]\s+/, '');
    const m = b.match(/^([^:]{1,40}):\s+(.+)$/);
    if (m) rows.push({ label: m[1]!.trim(), value: m[2]!.trim() });
    else footer.push(b);
  }
  return { rows, footer: footer.join(' ').trim() };
}

function priceNum(s: string): number | null {
  const m = (s || '').replace(/[, ]/g, '').match(/(\d+(?:\.\d+)?)/);
  return m ? Number(m[1]) : null;
}
const audioLabel = (url: string) => { const m = url.match(/p(\d+)_d(\d+)/i); return m ? `Pattern ${+m[1]!} · Day ${+m[2]!}` : 'Audio sample'; };
const pageLabel = (url: string) => { const m = url.match(/_p(\d+)\./i); return m ? `Pattern ${+m[1]!}` : 'Sample page'; };

export function serializeStorePayload(
  s: StoreCopy,
  meta: { line: string; language: string; level: string },
): Record<string, unknown> {
  const b = s.blocks;
  let languageName = meta.language, levelName = meta.level;
  const parts = (s.title || '').split(':');
  if (parts.length >= 2) { languageName = parts[0]!.trim(); levelName = parts.slice(1).join(':').trim(); }

  const b9 = (b.b9 || '').trim();
  const socialPending = !b9 || /needs\b|testimonial|^\*?\[/i.test(b9);

  const landing_blocks: Array<Record<string, unknown>> = [
    { role: 'audience', text: b.b1 || '' },
    { role: 'headline', text: b.b2 || '' },
    { role: 'subheadline', ...splitBlock(b.b3 || '') },
    { role: 'intrigue', text: b.b4 || '' },
    { role: 'problem', text: b.b5 || '' },
    { role: 'marketplace', ...splitBlock(b.b6 || '') },
    { role: 'credentials', text: b.b7 || '' },
    { role: 'benefits', list: splitBlock(b.b8 || '').list },
    socialPending ? { role: 'social_proof', pending: true } : { role: 'social_proof', text: b9 },
    { role: 'godfather', text: b.b10 || '' },
    { role: 'value_stack', ...valueStack(b.b12 || '') },
    { role: 'price_reveal', text: b.b13 || '' },
    { role: 'scarcity', text: b.b14 || '' },
    { role: 'guarantee', text: b.b15 || '' },
    { role: 'cta', text: b.b16 || '' },
    { role: 'ps', text: b.b17 || '' },
  ];

  const bonusNames = nonEmpty(s.bonuses);
  const bonusCovers = nonEmpty(s.bonus_covers);

  return {
    product_id: s.product_id || `${meta.line}:${meta.language}:${meta.level}`,
    line: meta.line, language: meta.language, language_name: languageName,
    level: meta.level, level_name: levelName,
    title: s.title, author: s.author || 'Glossika',
    formats: meta.line === 'audio' ? ['zip'] : ['pdf', 'epub'],
    status: 'active',
    cover_image_url: s.cover_image_url || null,
    assets: {
      audio_samples: nonEmpty(s.sample_audio).map((url) => ({ label: audioLabel(url), url })),
      sample_pages: nonEmpty(s.sample_pages).map((url) => ({ label: pageLabel(url), url })),
    },
    landing_blocks,
    bonuses: bonusNames.map((name, i) => ({ name, cover_image_url: bonusCovers[i] || null })),
    fund: { upsell_label: s.upsell.trim(), footer_line: s.fund_line.trim() },
    success_page: { how_to_use: s.success_instructions.trim(), subscription_upsell: s.success_upsell.trim() },
    suggested_prices: {
      your_price_usd: priceNum(s.vars.your_price),
      bundle_price_usd: priceNum(s.vars.bundle_price),
      anchor_high_usd: priceNum(s.vars.anchor_high),
      anchor_low_usd: priceNum(s.vars.anchor_low),
    },
  };
}
